import { describe, type OpenedSong } from '../library/index.js';
import { loadSongPackParts } from '../songpack/index.js';

/** Audio may be replaced in place. A new composition needs matching offline charts. */
export const DEMO_CONFIG = Object.freeze({
  version: 1,
  packId: 'mandalizm_demo_v1',
  seed: 20260912,
  difficulty: 'normal' as const,
  waveSpeed: 1,
  durationSeconds: 32,
  audioPath: '/assets/demo/mandalizm_demo.wav',
  packPath: '/assets/demo/songpack/',
});

/** In-memory only: demo neither requires nor adds anything to the user's library. */
export class DemoLoader {
  private pending: Promise<OpenedSong> | null = null;
  private preparing: Promise<void> | null = null;
  prepared: { readonly song: OpenedSong; readonly buffer: AudioBuffer } | null = null;

  constructor(private readonly request: typeof fetch = (input, init) => fetch(input, init),
    private readonly config: Readonly<{ packId: string; durationSeconds: number; audioPath: string; packPath: string; coverPath?: string | undefined; requestTimeoutMs?: number }> = DEMO_CONFIG) {}

  /** Fetch and decode before a gesture; retain the decoded buffer for replay. */
  preload(decode: (bytes: ArrayBuffer) => Promise<AudioBuffer>): Promise<void> {
    if (this.prepared !== null) return Promise.resolve();
    if (this.preparing !== null) return this.preparing;
    this.preparing = this.load().then(async (song) => {
      const bytes = new Uint8Array(song.pack.music.byteLength);
      bytes.set(song.pack.music);
      let timeout: ReturnType<typeof setTimeout> | undefined;
      try {
        const buffer = await Promise.race([
          decode(bytes.buffer),
          new Promise<never>((_, reject) => {
            timeout = setTimeout(() => reject(new Error('Demo audio decode timed out. Press PLAY DEMO to retry.')), 15000);
          }),
        ]);
        this.prepared = { song, buffer };
      } finally {
        if (timeout !== undefined) clearTimeout(timeout);
      }
    }).finally(() => { this.preparing = null; });
    return this.preparing;
  }

  load(): Promise<OpenedSong> {
    if (this.pending !== null) return this.pending;
    this.pending = this.read().catch((cause: unknown) => {
      // Failed downloads must be retryable, not a permanently cached rejection.
      this.pending = null;
      const detail = cause instanceof Error ? cause.message : String(cause);
      throw new Error(`Demo could not load. Please try PLAY DEMO again or choose CUSTOM PLAY. ${detail}`);
    });
    return this.pending;
  }

  private async read(): Promise<OpenedSong> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), this.config.requestTimeoutMs ?? 15000);
    const bytes = async (path: string): Promise<Uint8Array> => {
      const response = await this.request(path, {
        signal: controller.signal,
        credentials: 'omit',
        redirect: 'error',
        mode: 'same-origin',
      });
      if (!response.ok) throw new Error(`${path}: HTTP ${response.status}`);
      return new Uint8Array(await response.arrayBuffer());
    };
    try {
      const [manifest, easy, normal, hard, music, cover] = await Promise.all([
        bytes(`${this.config.packPath}manifest.json`),
        bytes(`${this.config.packPath}easy.chart`),
        bytes(`${this.config.packPath}normal.chart`),
        bytes(`${this.config.packPath}hard.chart`),
        bytes(this.config.audioPath),
        this.config.coverPath ? bytes(this.config.coverPath) : Promise.resolve(null),
      ]);
      const pack = loadSongPackParts({ manifest, easy, normal, hard, music, cover });
      if (pack.manifest.packId !== this.config.packId) throw new Error('Demo pack version mismatch.');
      if (pack.charts.normal.events.length === 0) throw new Error('Demo chart is empty.');
      return {
        entry: describe(pack, { source: 'builtin', durationSeconds: this.config.durationSeconds }),
        pack,
        video: null,
      };
    } finally {
      clearTimeout(timeout);
      controller.abort();
    }
  }
}
